import { useState, useEffect, useRef, useMemo } from 'react';
import Select from 'react-select';
import ReactPlayer from 'react-player';

import { api, useAPIContext } from '../../../api/context';
import { useFPContext } from '../../../utils/fingerprint';
import currency from '../../../utils/currency';
import toast from '../../../utils/toast';
import Base from '../../Base';
import photoExampleImg from '../../../assets/photo-example.png';
import styles from './GroupUrlSubmitPlus.module.scss';

const selectStyles = {
  container: (base) => ({
    ...base,
    marginTop: '10px',
  }),
  control: (base, state) => ({
    ...base,
    border: 'none',
    borderRadius: '50px',
    padding: '6px 12px',
    fontWeight: 600,
    boxShadow: state.isFocused ? '0 0 0 3px #3956de' : 'none',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '16px',
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    fontWeight: 500,
    cursor: 'pointer',
    backgroundColor: state.isSelected ? '#3956de' : state.isFocused ? '#f0f0f0' : 'white',
    color: state.isSelected ? 'white' : 'black',
  }),
};

const STATUS_LABEL = {
  'S': { label: 'Under Review', className: 'submitted' },
  'D': { label: 'Accepted', className: 'accepted' },
  'R': { label: 'Needs Revision', className: 'rejected' },
  'A': { label: 'Assigned', className: 'assigned' },
};

const extractTikTokVideoId = (url) => {
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : null;
};

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const absoluteMediaUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url}`;
};

const DEFAULT_LOCKED_MESSAGE = 'This activity is currently locked.';

const ActivitySubmitForm = ({ campaign, activity, handle, hashtagsRef, groupProducts, expanded, onToggle }) => {

  const { token } = useAPIContext();

  /*
   * data
   */

  const [ dataLoading, setDataLoading ] = useState(false);
  const [ dataLoaded, setDataLoaded ] = useState(false);
  const [ data, setData ] = useState();

  const [ hashtags, setHashtags ] = useState('');
  const [ submitPlaceholder, setSubmitPlaceholder ] = useState('');

  useEffect(() => {

    if (!expanded || dataLoaded) return;

    setDataLoading(true);

    api(token).v1.campaigns.activities
      .obtain({
        campaign: campaign.id,
        activity: activity.id,
      })
      .then(res => {
        const data = res.body.data;

        setDataLoading(false);
        setDataLoaded(true);
        setData(data);

        setHashtags(
          (data.config.hashtags || [])
          .join(' ')
        );

        if (data.result?.data?.value_cleaned) {
          setSubmitPlaceholder(data.result.data.value_cleaned);
        }
      })
      .catch(err => {
        setDataLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign.id, activity.id, expanded, dataLoaded]);

  /*
   * product selection
   */

  const [ selectedCategory, setSelectedCategory ] = useState(null);
  const [ selectedBrand, setSelectedBrand ] = useState(null);
  const [ selectedProduct, setSelectedProduct ] = useState(null);

  const products = useMemo(() => groupProducts || [], [groupProducts]);

  const categoryOptions = useMemo(() =>
    [...new Set(products.map(p => p.category).filter(Boolean))]
      .map(c => ({ value: c, label: c })),
    [products]
  );

  const showCategoryDropdown = categoryOptions.length > 1;

  const brandOptions = useMemo(() => {
    const filtered = selectedCategory
      ? products.filter(p => p.category === selectedCategory.value)
      : products;
    return [...new Set(filtered.map(p => p.brand))]
      .map(b => ({ value: b, label: b }));
  }, [products, selectedCategory]);

  const productOptions = useMemo(() => {
    let filtered = products;
    if (selectedCategory) filtered = filtered.filter(p => p.category === selectedCategory.value);
    if (selectedBrand) filtered = filtered.filter(p => p.brand === selectedBrand.value);
    return filtered.map(p => ({ value: p.gtin, label: p.product }));
  }, [products, selectedCategory, selectedBrand]);

  const selectedGtin = selectedProduct?.value || '';

  /*
   * label / UPC
   */

  const fileInputRef = useRef(null);
  const [ selectedPhoto, setSelectedPhoto ] = useState(null);
  const [ photoPreview, setPhotoPreview ] = useState(null);

  /*
   * submit
   */

  const { fp } = useFPContext();

  const [ submitLoading, setSubmitLoading ] = useState(false);
  const [ submitValue, setSubmitValue ] = useState('');
  const [ submitError, setSubmitError ] = useState('');
  const [ showPhotoModal, setShowPhotoModal ] = useState(false);
  const [ customFieldValue, setCustomFieldValue ] = useState('');

  const uploadReceipt = data?.config?.upload_receipt || false;

  const handleSubmitClick = () => {

    if (activity.user.enable === false) {
      toast.show(
        toast.KIND.WARNING,
        activity.expr_message || DEFAULT_LOCKED_MESSAGE,
      );
      return;
    }

    if (activity.action === 'url_submit_plus' && !/\S+\.\S+/.test(submitValue)) {
      setSubmitError('Please enter a valid URL.');
      return;
    }

    if (products.length > 0 && !selectedGtin) {
      setSubmitError('Please select a product.');
      return;
    }

    if (uploadReceipt && !selectedPhoto) {
      setSubmitError('Please upload a receipt.');
      return;
    }

    if (data?.config?.custom_field_enabled && data?.config?.custom_field_required && !customFieldValue.trim()) {
      setSubmitError(`${data.config.custom_field_label} is required.`);
      return;
    }

    setSubmitError('');

    if (uploadReceipt) {
      submit(selectedPhoto);
      return;
    }

    setSelectedPhoto(null);
    setPhotoPreview(null);
    setShowPhotoModal(true);
  };

  const submit = (photo) => {

    setShowPhotoModal(false);
    setSubmitLoading(true);

    fp.get().then(result => {

      const form = new FormData();
      form.append('data_json', JSON.stringify({
        value: submitValue,
        gtin: selectedGtin,
        gtin_manual: '',
        ...(data?.config?.custom_field_enabled && { custom_field: customFieldValue }),
      }));
      form.append('fingerprint', result.visitorId);

      if (photo) {
        form.append('media', photo);
      }

      api(token).v1.campaigns.activities.submit
        .post(
          {
            campaign: campaign.id,
            activity: activity.id,
          },
          form,
        )
        .then(res => {
          setSubmitLoading(false);

          if (res.status === 200) {
            handle(activity, res.body);
            onToggle();
            toast.show(toast.KIND.SUCCESS, 'Video link submitted successfully.');
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    });
  };

  /*
   * content
   */

  const statusInfo = STATUS_LABEL[activity.user.status];
  const isLocked = activity.user.enable === false;

  const rowButtonLabel = isLocked
    ? 'Locked'
    : activity.user.status === 'D'
      ? 'Completed'
      : (activity.user.status === 'S' || activity.user.status === 'R')
        ? 'Edit'
        : 'Add';

  return (
    <div className={`${styles.activity} ${expanded ? styles.expanded : ''}`}>
      <div className={styles.activityRow} onClick={onToggle}>
        <div className={styles.rowLeft}>
          {
            activity.reward.currency &&
            activity.reward.amount && (
              <span className={styles.rowReward}>
                {currency.repr(activity.reward.currency)}
                {activity.reward.amount}
              </span>
            )
          }
          <span className={styles.rowTitle}>{activity.title.external}</span>
        </div>
        <div className={styles.rowRight}>
          {
            statusInfo && (
              <span className={`${styles.rowStatus} ${styles[statusInfo.className]}`}>
                {statusInfo.label}
              </span>
            )
          }
          <span className={`${styles.rowBtn} ${activity.user.status === 'D' ? styles.completed : ''}`}>
            {rowButtonLabel}
          </span>
        </div>
      </div>

      {expanded && (
        <>
          {
            activity.user.comment &&
            activity.user.status === 'R' && (
              <div className={styles.comment}>
                {activity.user.comment}
              </div>
            )
          }

          {
            dataLoading &&
            <div className={styles.loading}>
              Loading...
            </div>
          }

          {
            data && (
          <>
            {
              data.instr && (
                <div
                  className={styles.instr}
                  dangerouslySetInnerHTML={{
                    __html: data.instr
                  }}
                />
              )
            }

            {
              hashtags && (
                <div className={styles.field}>
                  <label>
                    Use these hashtags in your <i>Video Review</i> caption
                  </label>

                  <input
                    className={styles.copy}
                    type='text'
                    disabled={true}
                    aria-label='hashtags'
                    value={hashtags}
                  />

                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(hashtags)
                        .then(() => {
                          toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                        })
                        .catch(() => {
                          toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                        });
                    }}>

                    Copy Hashtags

                  </button>
                </div>
              )
            }

            {products.length > 0 && (
              <div className={styles.field}>
                <label>Select a product</label>

                {showCategoryDropdown && (
                  <Select
                    styles={selectStyles}
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={(option) => {
                      setSelectedCategory(option);
                      setSelectedBrand(null);
                      setSelectedProduct(null);
                    }}
                    placeholder='All Categories'
                    isClearable
                    aria-label='category'
                  />
                )}

                <Select
                  styles={selectStyles}
                  options={brandOptions}
                  value={selectedBrand}
                  onChange={(option) => {
                    setSelectedBrand(option);
                    setSelectedProduct(null);
                  }}
                  placeholder='All Brands'
                  isClearable
                  aria-label='brand'
                />

                <Select
                  styles={selectStyles}
                  options={productOptions}
                  value={selectedProduct}
                  onChange={(option) => setSelectedProduct(option)}
                  placeholder='Select Product'
                  isClearable
                  aria-label='product'
                />
              </div>
            )}

            {uploadReceipt && (
              <div className={styles.field}>
                <label>Upload your receipt</label>

                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0] || null;
                    if (file) {
                      setSelectedPhoto(file);
                      setPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />

                {photoPreview && (
                  <img
                    className={styles.receiptPreview}
                    src={photoPreview}
                    alt='Receipt'
                  />
                )}

                <button
                  type='button'
                  disabled={isLocked}
                  onClick={() => fileInputRef.current?.click()}>
                  {selectedPhoto ? 'Change Receipt' : 'Upload Receipt'}
                </button>
              </div>
            )}

            {data?.config?.custom_field_enabled && (
              <div className={styles.field}>
                <label>
                  {data.config.custom_field_label}
                </label>

                <input
                  type='text'
                  aria-label='custom field'
                  disabled={submitLoading || isLocked}
                  value={customFieldValue}
                  onChange={(event) => setCustomFieldValue(event.target.value)}
                />
              </div>
            )}

            <div className={styles.field}>
              <label>
                Paste your <i>Video Review</i> link
                <span>Use Share &rarr; Copy link</span>
              </label>

              <input
                type='url'
                aria-label='link'
                disabled={submitLoading || isLocked}
                placeholder={submitPlaceholder}
                value={submitValue}
                onChange={(event) => setSubmitValue(event.target.value)}
              />

              <button
                type='button'
                disabled={submitLoading}
                onClick={() => handleSubmitClick()}>

                {isLocked
                  ? 'Locked'
                  : activity.user.status === 'S' || activity.user.status === 'R'
                    ? 'Re-submit' : 'Submit'}

              </button>

              {
                submitError && (
                  <div className={styles.error}>
                    {submitError}
                  </div>
                )
              }

              {
                hashtagsRef && (
                  <span
                    className={styles.hashtagsLink}
                    onClick={() => hashtagsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                    Use Hashtags Above ☝️
                  </span>
                )
              }
            </div>

            {!uploadReceipt && showPhotoModal && (
              <div className={styles.photoOverlay}>
                <div className={styles.photoModal}>
                  <img
                    className={styles.photoExample}
                    src={photoExampleImg}
                    alt='Example: barcode, product label, or appliance label'
                  />
                  <p>
                    Please take a photo of the barcode or the label of your product.
                    This will increase your chances of your activity being approved.
                  </p>

                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0] || null;
                      if (file) {
                        setSelectedPhoto(file);
                        setPhotoPreview(URL.createObjectURL(file));
                      }
                    }}
                  />

                  {photoPreview && (
                    <img
                      className={styles.photoPreview}
                      src={photoPreview}
                      alt='Selected'
                    />
                  )}

                  {selectedPhoto ? (
                    <button
                      type='button'
                      onClick={() => submit(selectedPhoto)}>
                      Upload Photo
                    </button>
                  ) : (
                    <button
                      type='button'
                      onClick={() => fileInputRef.current?.click()}>
                      Upload Photo
                    </button>
                  )}

                  <span
                    className={styles.skip}
                    onClick={() => submit(null)}>
                    Skip
                  </span>
                </div>
              </div>
            )}

            {(data.config.sample_video_files?.length > 0 ||
              data.config.sample_videos?.length > 0) && (
              <div className={styles.videos}>
                <h3>✨ Check out these videos for inspiration! 🎬</h3>
                {data.config.sample_video_files?.map((url, index) => (
                  <div key={`f-${index}`}>
                    <ReactPlayer
                      src={absoluteMediaUrl(url)}
                      controls
                      width='330px'
                      height='587px'
                      style={{ display: 'block' }}
                      playsInline
                    />
                  </div>
                ))}
                {data.config.sample_videos?.map((url, index) => {
                  const videoId = extractTikTokVideoId(url);
                  if (!videoId) return null;
                  return (
                    <div key={`t-${videoId}`}>
                      <iframe
                        title={`Sample video ${index + 1}`}
                        src={`https://www.tiktok.com/embed/v2/${videoId}`}
                        loading={index > 0 ? 'lazy' : undefined}
                        allowFullScreen
                        allow='encrypted-media'
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )
      }
        </>
      )}
    </div>
  );
};

const GroupUrlSubmitPlus = ({ campaign, group, handle }) => {

  const _handle = (activity, data) => {
    handle(activity, data);
  };

  const hashtagsRef = useRef(null);
  const groupHashtags = (group.config?.hashtags || []).join(' ');
  const groupProducts = group.config?.products || [];

  const singleActivity = group.activities?.length === 1;
  const [ expandedId, setExpandedId ] = useState(
    singleActivity ? group.activities[0].id : null
  );

  return (
    <Base title='Submit'>
      <div className={styles.root}>
        {
          group.instr && (
            <div
              className={styles.groupInstr}
              dangerouslySetInnerHTML={{
                __html: group.instr
              }}
            />
          )
        }

        {
          groupHashtags && (
            <div ref={hashtagsRef} className={styles.field}>
              <label>
                Use these hashtags in your <i>Video Review</i> caption
              </label>

              <input
                className={styles.copy}
                type='text'
                disabled={true}
                aria-label='hashtags'
                value={groupHashtags}
              />

              <button
                type='button'
                onClick={() => {
                  navigator.clipboard.writeText(groupHashtags)
                    .then(() => {
                      toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                    })
                    .catch(() => {
                      toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                    });
                }}>

                Copy Hashtags

              </button>
            </div>
          )
        }

        <div className={styles.activities}>
          {
            group.activities &&
            group.activities.map(activity => (
              <ActivitySubmitForm
                key={activity.id}
                campaign={campaign}
                activity={activity}
                handle={_handle}
                hashtagsRef={groupHashtags ? hashtagsRef : null}
                groupProducts={groupProducts}
                expanded={expandedId === activity.id}
                onToggle={singleActivity ? () => {} : () => setExpandedId(prev => prev === activity.id ? null : activity.id)}
              />
            ))
          }
        </div>

        {(group.config?.sample_video_files?.length > 0 ||
          group.config?.sample_videos?.length > 0) && (
          <div className={styles.videos}>
            <h3>✨ Check out these videos for inspiration! 🎬</h3>
            {group.config.sample_video_files?.map((url, index) => (
              <div key={`f-${index}`}>
                <ReactPlayer
                  src={absoluteMediaUrl(url)}
                  controls
                  width='330px'
                  height='587px'
                  style={{ display: 'block' }}
                  playsInline
                />
              </div>
            ))}
            {group.config.sample_videos?.map((url, index) => {
              const videoId = extractTikTokVideoId(url);
              if (!videoId) return null;
              return (
                <div key={`t-${videoId}`}>
                  <iframe
                    title={`Sample video ${index + 1}`}
                    src={`https://www.tiktok.com/embed/v2/${videoId}`}
                    loading={index > 0 ? 'lazy' : undefined}
                    allowFullScreen
                    allow='encrypted-media'
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Base>
  );
};

export default GroupUrlSubmitPlus;
